import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import { apiRunWithAxios } from './APIRunWithAxios';
import { useDispatch, useSelector } from 'react-redux';
import TapIcon from "../../../../services/TapIcon";
import Loader from '../../../../components/ui/Loader/Loader';
import { authLoginAndGetJWTToken } from "../../../../store/actions/OmniComm/OmniCommAuthAction";
import DateService from "../../../../services/DateService";

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function TrackingMap({ formData }) {
    const dispatch = useDispatch();
    const { data } = useSelector(state => state.omniAuthLoginReducer);

    const [currentStateData, setCurrentStateData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [currentEquipment, setCurrentEquipment] = useState(null);
    const [mapError, setMapError] = useState(null);
    
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef({});
    const initAttempted = useRef(false);

    // Define processEquipmentData BEFORE using it in useMemo
    const processEquipmentData = useCallback((currentState) => {
        if (!currentState) return null;

        let lat = currentState?.latitude;
        let lng = currentState?.longitude;
        
        // Validate coordinates
        if (!lat || !lng || lat === 0 || lng === 0 || isNaN(lat) || isNaN(lng)) {
            lat = 20.5937; // Default India center
            lng = 78.9629;
        }

        return {
            // id      : currentState.imei || currentState.id || 'Unknown',
            name    : currentState?.regNum,
            lat     : Number(lat),
            lng     : Number(lng),
            speed   : currentState.speed || 0,
            ignition: currentState.currentIgn,
            direction: currentState.direction || 0,
            engineStatus: currentState.engineWork,
            eventDate : currentState?.date && DateService.newSecToDateTimeFormat(currentState?.date)
        };
    }, [currentStateData, data?.name]);

    const equipmentData = useMemo(() => {
        if (!currentStateData) return [];
        
        // Handle both single object and array responses
        const dataArray = Array.isArray(currentStateData) ? currentStateData : [currentStateData];
        
        const processedData = dataArray
            .map(item => processEquipmentData(item))
            .filter(item => item !== null);
            
        return processedData;
    }, [currentStateData, processEquipmentData]);

    useEffect(() => {
        if (data?.jwt) {
            getStatisticData()
        }
    }, [formData, data])

    // Initialize map
    useEffect(() => {
        if (!mapRef.current || mapInstance.current || initAttempted.current) return;
        initAttempted.current = true;
        
        try {
            const map = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
            
            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB',
                subdomains: 'abcd',
                maxZoom: 19,
                minZoom: 3
            }).addTo(map);
            
            L.control.zoom({
                position: 'topright'
            }).addTo(map);
            mapInstance.current = map;
            setMapReady(true);
            setMapError(null);
            
        } catch (error) {
            setMapError('Failed to initialize map');
            setMapReady(false);
        }
        
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
                initAttempted.current = false;
            }
        };
    }, [currentStateData]);

    // Update markers when equipment data changes
    useEffect(() => {
        if (!mapReady || !mapInstance.current || equipmentData.length === 0) return;
        
        const map = mapInstance.current;
        
        // Clear existing markers
        Object.values(markersRef.current).forEach(marker => {
            if (marker && map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        markersRef.current = {};
        
        // Create bounds to fit all markers
        const bounds = [];
        
        // Add new markers
        equipmentData.forEach(equipment => {
            const { lat, lng, id, name, speed, ignition, direction } = equipment;
            
            if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                bounds.push([lat, lng]);
                
                // Create custom marker icon
                const markerIcon = L.divIcon({
                    className: 'custom-marker',
                    html: `<div style="
                        background-color: ${ignition ? '#4CAF50' : '#f44336'};
                        width: 12px;
                        height: 12px;
                        border-radius: 50%;
                        border: 2px solid white;
                        box-shadow: 0 0 4px rgba(0,0,0,0.5);
                    "></div>`,
                    iconSize: [12, 12],
                    popupAnchor: [0, -6]
                });
                
                const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
                
                // Create popup content
                const popupContent = `
                    <div style="min-width: 200px;">
                        <strong>${name || id}</strong><br/>
                        Speed: ${speed} km/h<br/>
                        Ignition: ${ignition ? 'ON' : 'OFF'}<br/>
                        Direction: ${direction}°<br/>
                        Event Date: ${equipment.eventDate}
                    </div>
                `;
                
                marker.bindPopup(popupContent);
                
                marker.on('click', () => {
                    setCurrentEquipment(equipment);
                });
                
                markersRef.current[id] = marker;
            }
        });
        
        // Fit map bounds to show all markers
        if (bounds.length > 0) {
            const boundsObj = L.latLngBounds(bounds);
            map.fitBounds(boundsObj, { padding: [30, 30] });
        }
        
    }, [currentStateData, mapReady, equipmentData]);

    const getStatisticData = async () => {
        if (!formData?.asset_ids) {
            toast.error('Please select an asset', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        
        setLoading(true);
        setMapError(null);
        
        try {
            const payload = {
                ids: formData.asset_ids,
                parseAddress : false,
                // ids         :   [1201000825,1269000140,1269000138,1269000141,1269000142,1269000143]
                
            };
            
            const response = await apiRunWithAxios(
                "https://online.omnicomm.in/ls/api/v1/activity/vehicles",
                "POST",
                data?.jwt,
                {},
                payload
            );
            
            const results = response.data;
            
            if (results && results.length > 0) {
                setCurrentStateData(results);
                
                // Set first equipment as current for details view
                const firstEquipment = processEquipmentData(results[0]);
                if (firstEquipment) {
                    setCurrentEquipment(firstEquipment);
                }
            } else {
                setCurrentStateData(null);
                setCurrentEquipment(null);
                toast.error('No data found for the selected criteria', { position: toast.POSITION.TOP_RIGHT });
            }
            
        } catch (error) {
            
            if (error?.response?.status === 401 && error?.response?.data?.error === "Unauthorized") {
                const result = await swal({
                    title: "Session Expired",
                    text: "Your session has expired. Please refresh to continue.",
                    icon: "warning",
                    buttons: ["Cancel", "Refresh"],
                    dangerMode: true
                });
                
                if (result) {
                    dispatch(authLoginAndGetJWTToken());
                }
            } else {
                toast.error(error?.response?.data?.message || 'Failed to fetch data', { 
                    position: toast.POSITION.TOP_RIGHT 
                });
                setMapError('Failed to load location data');
            }
            
            setCurrentStateData(null);
            setCurrentEquipment(null);
            
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        if (!loading) {
            getStatisticData();
        }
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-8">
                        <h6 className="fw-bold  mb-0">
                            <TapIcon.FontAwesomeIcon icon={TapIcon.faMap} className="me-2" />
                            Asset Location Status
                        </h6>
                    </div>
                    <div className="col-4 text-end">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <TapIcon.FontAwesomeIcon 
                                icon={TapIcon.faSyncAlt} 
                                className={loading ? "fa-spin" : ""} 
                            />
                        </button>
                    </div>
                </div>

                <div className="row g-3">
                    {/* <div className="col-md-5">
                        <div className="border rounded overflow-auto" style={{ maxHeight: "400px" }}>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Loader />
                                    <p className="text-muted mt-2 mb-0">Loading data...</p>
                                </div>
                            ) : (
                                renderEquipmentDetails()
                            )}
                        </div>
                    </div> */}
                    {    
                        <div className="col-md-12">
                            <div className="map-container" style={{
                                height: "550px",
                                position: "relative",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #dee2e6",
                                backgroundColor: "#f8f9fa"
                            }}>
                                <div
                                    ref={mapRef}
                                    style={{ width: "100%", height: "100%" }}
                                />

                                {!mapReady && !mapError && (
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading map...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Initializing map...</p>
                                    </div>
                                )}

                                {mapError && (
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                        <TapIcon.FontAwesomeIcon icon={TapIcon.faExclamationTriangle} size="3x" className="text-warning mb-2" />
                                    </div>
                                )}

                                {mapReady && equipmentData.length === 0 && !loading && (
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                        <p className="text-muted">No data available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}